// Claims model (PLAN §2 "Claims", "Sources and attribution", §4 Graph). Everything known about an entity is a
// claim with provenance. Claims are append-only: an edit writes a new claim that supersedes the old one; review
// decisions change only the status fields. Profiles are a materialized view of the current, non-rejected claims.

import type { ClusterRef } from './clusters';
import type { EntityStyle } from './colors';
import type { EntityKind } from './schema/types';
import type { SourceClass } from './source-registry';
import { SENSITIVITIES, type Sensitivity } from './uploads';

export const CLAIM_KINDS = ['fact', 'statement', 'assessment', 'allegation'] as const;
export type ClaimKind = (typeof CLAIM_KINDS)[number];
export const isClaimKind = (k: unknown): k is ClaimKind => CLAIM_KINDS.includes(k as ClaimKind);
export const CLAIM_KIND_LABELS: Record<ClaimKind, string> = {
  fact: 'Fact',
  statement: 'Statement',
  assessment: 'Assessment',
  allegation: 'Allegation',
};

export const REVIEW_STATUSES = ['unreviewed', 'verified', 'rejected', 'disputed'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];
export const isReviewStatus = (s: unknown): s is ReviewStatus => REVIEW_STATUSES.includes(s as ReviewStatus);

export const isSensitivity = (s: unknown): s is Sensitivity => SENSITIVITIES.includes(s as Sensitivity);
/** The highest of several sensitivities (a claim backed by any confidential source is confidential). */
export const maxSensitivity = (...s: (Sensitivity | null | undefined)[]): Sensitivity =>
  s.includes('confidential') ? 'confidential' : 'standard';

export const ATTRIBUTION_LEVELS = ['on_record', 'background', 'off_record', 'anonymous'] as const;
export type AttributionLevel = (typeof ATTRIBUTION_LEVELS)[number];
export const isAttributionLevel = (a: unknown): a is AttributionLevel => ATTRIBUTION_LEVELS.includes(a as AttributionLevel);
export const ATTRIBUTION_LABELS: Record<AttributionLevel, string> = {
  on_record: 'On record',
  background: 'Background',
  off_record: 'Off record',
  anonymous: 'Anonymous source',
};

export const VERIFICATION_FLAGS = ['documented', 'semi_documented', 'unverified'] as const;
export type VerificationFlag = (typeof VERIFICATION_FLAGS)[number];
export const isVerificationFlag = (f: unknown): f is VerificationFlag => VERIFICATION_FLAGS.includes(f as VerificationFlag);

export const SOURCE_TYPES = ['publication', 'official_document', 'register', 'interview', 'social_post', 'website', 'other'] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];
export const isSourceType = (t: unknown): t is SourceType => SOURCE_TYPES.includes(t as SourceType);
export const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  publication: 'Publication',
  official_document: 'Official document',
  register: 'Register',
  interview: 'Interview (a person)',
  social_post: 'Social media post',
  website: 'Website',
  other: 'Other',
};

export const IDENTITY_CONFIDENCE = ['confirmed', 'probable', 'anonymous'] as const;
export type IdentityConfidence = (typeof IDENTITY_CONFIDENCE)[number];
export const isIdentityConfidence = (v: unknown): v is IdentityConfidence => IDENTITY_CONFIDENCE.includes(v as IdentityConfidence);

export const IDENTITY_STATUSES = ['identified', 'probable', 'unidentified'] as const;
export type IdentityStatus = (typeof IDENTITY_STATUSES)[number];
export const isIdentityStatus = (v: unknown): v is IdentityStatus => IDENTITY_STATUSES.includes(v as IdentityStatus);

export const NAME_KINDS = ['primary', 'alias', 'transliteration', 'former'] as const;
export type NameKind = (typeof NAME_KINDS)[number];
export const isNameKind = (v: unknown): v is NameKind => NAME_KINDS.includes(v as NameKind);

// ─── Reliability grading (A–F × 1–6) ─────────────────────────────────────────

export const SOURCE_RELIABILITY = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
export type SourceReliability = (typeof SOURCE_RELIABILITY)[number];
export const isSourceReliability = (v: unknown): v is SourceReliability => SOURCE_RELIABILITY.includes(v as SourceReliability);
export const isCredibility = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 6;

export const SOURCE_RELIABILITY_LABELS: Record<SourceReliability, string> = {
  A: 'Completely reliable',
  B: 'Usually reliable',
  C: 'Fairly reliable',
  D: 'Not usually reliable',
  E: 'Unreliable',
  F: 'Reliability cannot be judged',
};
export const CREDIBILITY_LABELS: Record<number, string> = {
  1: 'Confirmed by other sources',
  2: 'Probably true',
  3: 'Possibly true',
  4: 'Doubtful',
  5: 'Improbable',
  6: 'Truth cannot be judged',
};

export interface ReliabilityGrade {
  /** A–F, or null when only the credibility is known. */
  source: SourceReliability | null;
  /** 1–6, or null when only the source letter is known. */
  credibility: number | null;
  /** The wording that came with the grade ("High/Confirmed"), kept verbatim. */
  note: string | null;
}

/** "B2", "B", "2" → display code. */
export const formatGrade = (g: Pick<ReliabilityGrade, 'source' | 'credibility'>): string =>
  `${g.source ?? ''}${g.credibility ?? ''}` || '—';

/**
 * Parses reliability as written in profile sheets: "A2", "A2 — High/Confirmed", "b-3", "Reliability: C3 (probable)".
 * Text without a code ("High") is kept as the note with no code.
 */
export function parseReliability(input: string): ReliabilityGrade | null {
  const raw = input.trim();
  if (!raw) return null;
  const m = raw.match(/(?:^|[^\p{L}\p{N}])([A-F])\s*[-/.]?\s*([1-6])(?![\p{L}\p{N}])/iu) ?? raw.match(/^([A-F])(?![\p{L}\p{N}])/iu);
  if (!m) return { source: null, credibility: null, note: raw };
  const source = (m[1]?.toUpperCase() ?? null) as SourceReliability | null;
  const credibility = m[2] ? Number(m[2]) : null;
  const note =
    (raw.slice(0, m.index ?? 0) + raw.slice((m.index ?? 0) + m[0].length))
      .replace(/^[\s\p{P}\p{S}]*(reliability|grade|fiabilit[ée])?[\s:—–-]*/iu, '')
      .replace(/^[\s\p{P}\p{S}]+|[\s\p{P}\p{S}]+$/gu, '')
      .replace(/^\(|\)$/g, '')
      .trim() || null;
  return { source, credibility, note };
}

// ─── API shapes ──────────────────────────────────────────────────────────────

/** Reserved predicate for tags (a tag on an entity is a claim with provenance). */
export const TAG_PREDICATE = 'tag';

export interface EvidenceInput {
  /** Uploaded document the claim was read from (Library). */
  documentId?: string | null;
  /** Page, char range, bbox, audio timestamp, sheet!row:column. */
  locator?: Record<string, unknown> | null;
  /** Short verbatim snippet. */
  quote?: string | null;
  /** Source cited inside that document (e.g. a Le Monde article cited in an analyst sheet). */
  citedSourceId?: string | null;
  /** Sensitivity of the document, when the caller knows it (the claim inherits the highest). */
  sensitivity?: Sensitivity | null;
}

export interface Evidence {
  id: string;
  claimId: string;
  documentId: string | null;
  locator: Record<string, unknown> | null;
  quote: string | null;
  citedSourceId: string | null;
  createdAt: number;
  createdBy: string | null;
}

export interface ClaimInput {
  /** Field key (attribute / table row) or relation type key; `tag` for tags. */
  predicate: string;
  /** Raw value: validated and normalized by the field's data type (dates, amounts, ratings parsed from text). */
  value?: unknown;
  /** Other end of a relation (required for relation types). */
  objectEntityId?: string | null;
  /** Defaults to the field's default claim kind, or `fact`. */
  claimKind?: ClaimKind;
  /** Relation period / attribute validity, free text ("2019–2022", "since 2021"). */
  period?: string | null;
  /** Relation amount (flow claims), free text ("€50,000", "undisclosed"). */
  amount?: string | null;
  confidence?: number | null;
  sensitivity?: Sensitivity;
  attributionLevel?: AttributionLevel;
  verificationFlag?: VerificationFlag | null;
  highImportance?: boolean;
  /** Author of an assessment (person or team name). */
  assessedBy?: string | null;
  sourceReliability?: SourceReliability | null;
  credibility?: number | null;
  /** Statements: the verbatim quote in its original language, and a translation. */
  originalText?: string | null;
  translation?: string | null;
  evidence?: EvidenceInput[];
  /** Manual entries are verified by their author; imports and AI start unreviewed. */
  status?: Extract<ReviewStatus, 'unreviewed' | 'verified'>;
  autoMatched?: boolean;
}

export interface Claim {
  id: string;
  subjectId: string;
  predicate: string;
  /** `attribute` (field), `row` (table-field row), `relation` (relation type), `tag`. */
  shape: ClaimShape;
  value: unknown;
  objectEntityId: string | null;
  validFrom: string | null;
  validTo: string | null;
  dateOpenEnd: boolean;
  datePrecision: string | null;
  /** The period as written. */
  periodText: string | null;
  amount: number | null;
  amountMin: number | null;
  amountMax: number | null;
  currency: string | null;
  amountQualifier: string | null;
  /** The amount as written. */
  amountText: string | null;
  confidence: number | null;
  status: ReviewStatus;
  sensitivity: Sensitivity;
  claimKind: ClaimKind;
  attributionLevel: AttributionLevel;
  verificationFlag: VerificationFlag | null;
  highImportance: boolean;
  assessedBy: string | null;
  sourceReliability: SourceReliability | null;
  credibility: number | null;
  originalText: string | null;
  translation: string | null;
  extractedBy: string;
  verifiedBy: string | null;
  verifiedAt: number | null;
  autoMatched: boolean;
  supersedesId: string | null;
  /** Set when a later claim replaced this one (derived, not stored). */
  supersededById: string | null;
  createdAt: number;
  createdBy: string | null;
  evidence?: Evidence[];
}

export const CLAIM_SHAPES = ['attribute', 'row', 'relation', 'tag'] as const;
export type ClaimShape = (typeof CLAIM_SHAPES)[number];

export interface CitedSource {
  id: string;
  sourceType: SourceType;
  /** Publisher (e.g. Le Monde) or the person interviewed, as an entity. */
  publisherEntityId: string | null;
  /** Publisher or person as written, when not (yet) linked to an entity. */
  publisherName: string | null;
  referenceNo: string | null;
  title: string | null;
  url: string | null;
  /** True when a publication/website/social post has no URL (flagged "missing URL"). */
  missingUrl: boolean;
  publishedAt: string | null;
  identityConfidence: IdentityConfidence | null;
  reliability: SourceReliability | null;
  /** Source registry: the outlet set by hand, or null (matched when read). */
  outletId: string | null;
  /** Source registry: the type set by hand, or null (the outlet's, else from the source type). */
  sourceClass: SourceClass | null;
  createdAt: number;
}

export interface EntityName {
  id: string;
  entityId: string;
  value: string;
  lang: string | null;
  script: string;
  normalized: string;
  latinTranslit: string | null;
  phoneticKey: string;
  isPrimary: boolean;
  kind: NameKind;
}

export interface EntitySummary {
  id: string;
  kind: EntityKind;
  typeId: string;
  typeKey: string;
  code: string;
  displayName: string;
  sensitivity: Sensitivity;
  reviewStatus: ReviewStatus;
  reliability: ReliabilityGrade;
  identityStatus: IdentityStatus;
  mergedIntoId: string | null;
  summary: string | null;
  updatedAt: number;
  /** The name that matched a search when it isn't the display name (alias, other script). */
  matchedName?: string;
  /** Current, non-rejected values of the requested fields (directory columns such as Camp), raw. */
  fieldValues?: Record<string, unknown[]>;
  /** Rules-mode look ("Color by: Rules"), from the cached `computed_style`; type mode is computed from the schema. */
  style?: EntityStyle;
  /** Cluster-mode look ("Color by: Cluster"): the entity's cluster, null when in none; absent = not loaded. */
  cluster?: ClusterRef | null;
}

/** Directory facet counts: each dimension counted with every other filter applied. */
export interface EntityFacets {
  kinds: Partial<Record<EntityKind, number>>;
  statuses: Partial<Record<ReviewStatus, number>>;
  types: Record<string, number>;
  /** Per enum field (e.g. camp): value key → count. */
  fields: Record<string, Record<string, number>>;
}

export interface Entity extends EntitySummary {
  names: EntityName[];
  templateVersion: string | null;
  createdAt: number;
  createdBy: string | null;
}

/** One value on a profile, with enough provenance to show kind, status and grade. */
export interface ProfileValue {
  claimId: string;
  value: unknown;
  /** Human-readable rendering of the value. */
  display: string;
  claimKind: ClaimKind;
  status: ReviewStatus;
  sensitivity: Sensitivity;
  attributionLevel: AttributionLevel;
  verificationFlag: VerificationFlag | null;
  highImportance: boolean;
  assessedBy: string | null;
  grade: string | null;
  objectEntityId: string | null;
  validFrom: string | null;
  validTo: string | null;
  evidenceCount: number;
  /** Responses and rebuttals linked to it (either way). */
  links?: number;
  /** Attached to this entity by a skipped review's automatic match (task 21), not yet confirmed by a person. */
  autoMatched?: boolean;
  /** Who added it (a user id; null for system imports). Authors may retract their own values. */
  createdBy: string | null;
}

export interface ProfileField {
  key: string;
  label: string;
  dataType: string;
  groupKey: string | null;
  multiValued: boolean;
  values: ProfileValue[];
  /** Two or more conflicting values on a single-valued field, or a value marked disputed. */
  disputed: boolean;
}

export interface ProfileRelation extends ProfileValue {
  predicate: string;
  label: string;
  /** `out`: this entity is the subject; `in`: this entity is the object (label is then the inverse). */
  direction: 'out' | 'in';
  otherEntity: {
    id: string;
    code: string;
    displayName: string;
    kind: EntityKind;
    typeId: string;
    typeKey: string;
    reviewStatus: ReviewStatus;
    sensitivity: Sensitivity;
    style?: EntityStyle;
    cluster?: ClusterRef | null;
  } | null;
  category: string | null;
}

/** The materialized profile: an entity's non-rejected, current claims (PLAN §2). */
export interface EntityProfile {
  entity: Entity;
  fields: Record<string, ProfileField>;
  relations: ProfileRelation[];
  tags: { key: string; label: string; colorSlot: number | null; claimId: string; status: ReviewStatus }[];
  /** Keys of disputed fields, and ids of disputed relations. */
  disputed: string[];
  /** Counts for the header (unreviewed items are labelled in the UI). */
  counts: { claims: number; unreviewed: number; disputed: number; assessments: number; allegations: number };
  computedAt: number;
}

// ─── Merge / unmerge (task 22) ───────────────────────────────────────────────

/** What a merge will do, shown before confirming (side by side). */
export interface MergePreview {
  survivor: Entity;
  merged: Entity;
  /** Claims moving from the merged entity (as subject) and links pointing at it (as object). */
  counts: { claims: number; links: number; names: number; sources: number };
  /** Names of the merged entity the survivor doesn't have yet (they become aliases). */
  newNames: string[];
  /** Why the merge can't happen (e.g. different kinds); empty when it can. */
  blockers: string[];
}

/** One merge, as listed on both profiles. */
export interface MergeRecord {
  id: string;
  survivor: EntitySummary;
  merged: EntitySummary;
  counts: { claims: number; links: number; names: number; sources: number };
  createdAt: number;
  createdBy: string | null;
  undoneAt: number | null;
  undoneBy: string | null;
  /** Unmerge is possible now (not undone, and the survivor isn't itself merged into something else). */
  canUnmerge: boolean;
  /** Why unmerge isn't possible, in plain words. */
  unmergeBlocker: string | null;
}
